import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Task, ACCENT_CLASSES, AccentColor } from '../../types/work';
import { format, differenceInDays, addDays, startOfDay } from 'date-fns';

interface Props {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
}

const STATUS_BAR_COLOR: Record<string, AccentColor> = {
  todo: 'gray',
  in_progress: 'violet',
  in_review: 'sky',
  blocked: 'red',
  completed: 'emerald',
  cancelled: 'gray',
};

export const GanttChart: React.FC<Props> = ({ tasks, onTaskClick }) => {
  const { darkMode } = useTheme();

  // Calculate time bounds from task dates
  const { startDate, endDate, totalDays } = useMemo(() => {
    const now = new Date();
    let min = now;
    let max = addDays(now, 14); // minimum 2 weeks

    tasks.forEach(t => {
      if (t.scheduled_at) {
        const d = new Date(t.scheduled_at);
        if (d < min) min = d;
      }
      if (t.due_date) {
        const d = new Date(t.due_date);
        if (d > max) max = d;
        if (d < min) min = d;
      }
      if (t.created_at) {
        const d = new Date(t.created_at);
        if (d < min) min = d;
      }
    });

    // Add padding
    const start = startOfDay(addDays(min, -2));
    const end = startOfDay(addDays(max, 3));
    const days = Math.max(7, differenceInDays(end, start));

    return { startDate: start, endDate: end, totalDays: days };
  }, [tasks]);

  // Today position as percentage
  const todayPct = useMemo(() => {
    const today = startOfDay(new Date());
    const days = differenceInDays(today, startDate);
    return Math.max(0, Math.min(100, (days / totalDays) * 100));
  }, [startDate, totalDays]);

  // Generate date labels for the header
  const dateLabels = useMemo(() => {
    const labels: { date: Date; pct: number }[] = [];
    const step = Math.max(1, Math.floor(totalDays / 10)); // ~10 labels
    for (let i = 0; i <= totalDays; i += step) {
      const date = addDays(startDate, i);
      labels.push({ date, pct: (i / totalDays) * 100 });
    }
    return labels;
  }, [startDate, totalDays]);

  // Task bar calculation
  const getTaskBar = (task: Task) => {
    const barStart = task.scheduled_at ? new Date(task.scheduled_at) : task.created_at ? new Date(task.created_at) : new Date();
    const barEnd = task.due_date ? new Date(task.due_date) : addDays(barStart, 3); // default 3-day bar if no due date

    const startPct = Math.max(0, (differenceInDays(barStart, startDate) / totalDays) * 100);
    const widthPct = Math.max(1.5, (differenceInDays(barEnd, barStart) / totalDays) * 100); // min width 1.5%

    return { startPct, widthPct };
  };

  const sortedTasks = useMemo(() =>
    [...tasks]
      .filter(t => t.status !== 'cancelled')
      .sort((a, b) => {
        const aDate = a.scheduled_at || a.due_date || a.created_at || '';
        const bDate = b.scheduled_at || b.due_date || b.created_at || '';
        return aDate.localeCompare(bDate);
      }),
    [tasks],
  );

  return (
    <div className={`rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Date header */}
      <div className={`relative h-8 border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        {dateLabels.map((label, i) => (
          <span
            key={i}
            className={`absolute text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
            style={{ left: `${Math.max(2, label.pct)}%`, top: '50%', transform: 'translateY(-50%)' }}
          >
            {format(label.date, 'MMM d')}
          </span>
        ))}
        {/* Today marker label */}
        <span
          className="absolute text-[10px] font-semibold text-amber-400"
          style={{ left: `${todayPct}%`, top: '2px', transform: 'translateX(-50%)' }}
        >
          today
        </span>
      </div>

      {/* Task rows */}
      <div className="relative">
        {/* Today line */}
        <div
          className="absolute top-0 bottom-0 w-px bg-amber-500/40 z-10"
          style={{ left: `${todayPct}%` }}
        />

        {sortedTasks.map(task => {
          const { startPct, widthPct } = getTaskBar(task);
          const accent = STATUS_BAR_COLOR[task.status] || 'gray';
          const colors = ACCENT_CLASSES[accent];
          const isCompleted = task.status === 'completed';

          return (
            <div
              key={task.id}
              onClick={() => onTaskClick(task)}
              className={`relative flex items-center h-10 border-b cursor-pointer transition-colors ${
                darkMode ? 'border-gray-700/50 hover:bg-gray-700/30' : 'border-gray-100 hover:bg-gray-50'
              } ${isCompleted ? 'opacity-50' : ''}`}
            >
              {/* Task label (fixed left) */}
              <div className={`w-48 shrink-0 px-3 text-xs font-medium truncate ${
                isCompleted ? 'line-through text-gray-500' : darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {task.title}
              </div>

              {/* Bar area */}
              <div className="flex-1 relative h-full">
                <div
                  className={`absolute top-1/2 -translate-y-1/2 h-5 rounded-full ${colors.fill} ${
                    isCompleted ? 'opacity-60' : 'opacity-80 hover:opacity-100'
                  } transition-opacity`}
                  style={{ left: `${startPct}%`, width: `${widthPct}%`, minWidth: '8px' }}
                  title={`${task.title}\n${task.scheduled_at ? `Start: ${format(new Date(task.scheduled_at), 'MMM d')}` : ''}\n${task.due_date ? `Due: ${format(new Date(task.due_date), 'MMM d')}` : ''}`}
                >
                  {/* Due date diamond marker at end of bar */}
                  {task.due_date && (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-2 h-2 rotate-45 bg-white/80" />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {sortedTasks.length === 0 && (
          <div className={`py-8 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            No tasks to display on timeline
          </div>
        )}
      </div>
    </div>
  );
};
